import type { CarrierStatus } from '../carrierEligibilityDisplay';

export interface StatusTone {
  readonly fg: string;
  readonly bg: string;
  readonly dot: string;
}

/**
 * Semantic status colors for the carrier rows, per color scheme. Light mode uses
 * deep 600/700 foregrounds on faint tints; dark mode uses bright 400 foregrounds
 * (a deep green/amber/red is unreadable on a dark surface) with slightly stronger
 * tints. The shell tells the panel which scheme it is rendering in.
 */
const LIGHT_TONE: Record<CarrierStatus, StatusTone> = {
  applicable: { fg: '#047857', bg: 'rgba(16, 185, 129, 0.15)', dot: '#10b981' },
  overridable: {
    fg: '#b45309',
    bg: 'rgba(245, 158, 11, 0.18)',
    dot: '#f59e0b',
  },
  unavailable: { fg: '#be123c', bg: 'rgba(244, 63, 94, 0.16)', dot: '#f43f5e' },
};

const DARK_TONE: Record<CarrierStatus, StatusTone> = {
  applicable: { fg: '#34d399', bg: 'rgba(16, 185, 129, 0.18)', dot: '#34d399' },
  // A softer gold with a light fill — the saturated amber block read as harsh orange in dark mode.
  overridable: {
    fg: '#fcd34d',
    bg: 'rgba(250, 204, 21, 0.12)',
    dot: '#fcd34d',
  },
  unavailable: { fg: '#fb7185', bg: 'rgba(244, 63, 94, 0.18)', dot: '#fb7185' },
};

export function getStatusTone(
  status: CarrierStatus,
  dark: boolean
): StatusTone {
  return (dark ? DARK_TONE : LIGHT_TONE)[status];
}

/** Solid pass/fail mark colors used in the rule breakdown, per color scheme. */
export function passColor(dark: boolean): string {
  return dark ? '#34d399' : '#059669';
}

export function failColor(dark: boolean): string {
  return dark ? '#fb7185' : '#e11d48';
}
